import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import * as Icons from 'lucide-react'
import type {LucideIcon} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import PortableTextRenderer from '@/components/blog/PortableTextRenderer'
import {urlForImage} from '@/lib/sanity'
import LanguageClaims from '@/components/language-page/LanguageClaims'
import LanguageTutors from '@/components/language-page/LanguageTutors'
import type {LanguagePage} from '@/types/languagePage'

interface Props {
  page: LanguagePage
}

const FALLBACK_ICON: LucideIcon = Icons.Sparkles

const renderIcon = (name: string, className: string) => {
  const Icon = (Icons as Record<string, unknown>)[name] as LucideIcon | undefined
  const Component = Icon ?? FALLBACK_ICON
  return <Component className={className} aria-hidden="true" />
}

const LanguagePageView = ({page}: Props) => {
  const {hero, intro, features, steps, faq, finalCta} = page
  const navigate = useNavigate()
  const {t} = useTranslation()

  return (
    <main>
      {/* Hero */}
      <section className="bg-gradient-to-b from-secondary/40 to-background">
        <div className="container mx-auto px-4 py-20 md:py-28 max-w-5xl text-center">
          <div className="text-left mb-6 md:mb-8">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate(-1)}>
              <Icons.ArrowLeft className="w-4 h-4 mr-1" />
              {t('nav.back')}
            </Button>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            {hero.heading}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            {hero.subheading}
          </p>
          <Button asChild size="lg">
            <Link to={hero.ctaHref}>{hero.ctaLabel}</Link>
          </Button>
          {/* Computed from the tutors, learn pages only (#1643) */}
          {page.variant === 'learn' ? <LanguageClaims slug={page.slug} /> : null}
          {hero.image?.asset ? (
            <div className="mt-12 max-w-4xl mx-auto">
              <img
                src={urlForImage(hero.image).width(1600).fit('max').auto('format').url()}
                alt={hero.image.alt ?? ''}
                className="rounded-2xl w-full h-auto"
              />
            </div>
          ) : null}
        </div>
      </section>

      {/* Intro */}
      {intro ? (
        <section className="container mx-auto px-4 py-20 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6 text-center">
            {intro.heading}
          </h2>
          <PortableTextRenderer value={intro.body} />
          {intro.image?.asset ? (
            <img
              src={urlForImage(intro.image).width(1400).fit('max').auto('format').url()}
              alt={intro.image.alt ?? ''}
              className="rounded-2xl w-full h-auto mt-10"
            />
          ) : null}
        </section>
      ) : null}

      {/* Features */}
      {features && features.length > 0 ? (
        <section className="bg-secondary/30">
          <div className="container mx-auto px-4 py-20 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <Card key={feature.title} className="border-border/60">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-foreground/5 flex items-center justify-center mb-4">
                      {renderIcon(feature.icon, 'w-5 h-5 text-foreground')}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Steps */}
      {steps && steps.length > 0 ? (
        <section className="container mx-auto px-4 py-20 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-12 text-center">
            {t('languagePage.stepsHeading')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div key={step.title} className="text-center">
                <div className="w-12 h-12 rounded-full bg-foreground text-background flex items-center justify-center mx-auto mb-4 font-display font-bold text-lg">
                  {idx + 1}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* The tutors a visitor can book, learn pages only (#1644) */}
      {page.variant === 'learn' ? <LanguageTutors slug={page.slug} /> : null}

      {/* FAQ */}
      {faq && faq.length > 0 ? (
        <section className="container mx-auto px-4 py-20 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-10 text-center">
            {t('languagePage.faqHeading')}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((item, idx) => (
              <AccordionItem key={item.question} value={`item-${idx}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent forceMount>
                  <PortableTextRenderer value={item.answer} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      {/* Final CTA */}
      <section className="bg-foreground text-background">
        <div className="container mx-auto px-4 py-20 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            {finalCta.heading}
          </h2>
          {finalCta.subheading ? (
            <p className="text-base md:text-lg opacity-80 mb-8">{finalCta.subheading}</p>
          ) : null}
          <Button asChild size="lg" variant="secondary">
            <Link to={finalCta.ctaHref}>{finalCta.ctaLabel}</Link>
          </Button>
        </div>
      </section>
    </main>
  )
}

export default LanguagePageView
